import React, {
  useEffect,
  useState,
  useContext,
  type FC,
  type SyntheticEvent,
  type MouseEvent,
  type FocusEvent,
  type PropsWithChildren,
} from 'react';

import { mqMin, BREAKPOINT } from '@lucid/theme';
import { isWindowAboveWidth } from '@lucid/utils';
import clsx from 'clsx';

import type { Lucid, CustomDrupal } from '../../types/types';
import Nav from './nav';
import NavDropdownMenu from './nav-dropdown-menu';
import Icon from '../icon/icon';
import Link, { type LinkProps } from '../link/link';

export interface NavDropdownProps extends LinkProps {
  /** Checks if the navbar is on the top most nav. */
  isTopNav?: boolean;
  /** The Dropdown specific links. */
  link: CustomDrupal.LinkInterface;
}

const NavDropdown: FC<PropsWithChildren<NavDropdownProps>> = ({
  asButton = true,
  className,
  isTopNav = false,
  link,
  variant = 'navLink',
  ...rest
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { isMenuOpen, setActiveDropdownCallback, setIsDropdownExpanded } = useContext(Nav.Context);

  const handleOnOpenDropdown = () => {
    setIsDropdownOpen(true);
    setIsDropdownExpanded(true);
    return setActiveDropdownCallback([setIsDropdownOpen]);

    // const threshold = 400;
    // const rect = evt.currentTarget.getBoundingClientRect();
    // const diff = document.body.offsetWidth - rect.left;
    // if (diff < threshold && shouldDisplayLeft) {
    //   setShouldDisplayLeft(false);
    // } else if (diff >= threshold && !shouldDisplayLeft) {
    //   setShouldDisplayLeft(true);
    // }
  };

  const handleOnCloseDropdown = () => setIsDropdownOpen(false);

  useEffect(() => {
    if (!isWindowAboveWidth(BREAKPOINT.TABLET_LANDSCAPE)) {
      if (isDropdownOpen && !isMenuOpen) {
        return handleOnCloseDropdown();
      }
    }
  }, [isDropdownOpen, isMenuOpen]);

  const handleOnClick = (e: SyntheticEvent) => {
    e.preventDefault();
    return isDropdownOpen ? handleOnCloseDropdown() : handleOnOpenDropdown();
  };

  const handleOnBlur = (e: FocusEvent) => {
    e.preventDefault();
    if (
      isWindowAboveWidth(BREAKPOINT.TABLET_LANDSCAPE) &&
      !e.currentTarget.parentNode?.contains(e.relatedTarget as Node) // focus moves from the button to the dropdown it controls
    ) {
      return handleOnCloseDropdown();
    }
  };

  const handleOnFocus = (e: FocusEvent) => {
    e.preventDefault();
    if (isWindowAboveWidth(BREAKPOINT.TABLET_LANDSCAPE)) {
      return handleOnOpenDropdown();
    }
  };

  const handleOnOpenHover = (e: MouseEvent) => {
    e.preventDefault();
    if (isWindowAboveWidth(BREAKPOINT.TABLET_LANDSCAPE)) {
      return handleOnOpenDropdown();
    }
  };

  const handleOnCloseHover = (e: MouseEvent) => {
    e.preventDefault();
    if (isWindowAboveWidth(BREAKPOINT.TABLET_LANDSCAPE)) {
      return handleOnCloseDropdown();
    }
  };

  return (
    <>
      <Link
        {...rest}
        aria-expanded={isDropdownOpen ? true : false}
        aria-haspopup="menu"
        asButton={asButton}
        css={(theme: Lucid.Theme) => [
          {
            label: 'navDropdownButton',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'relative',
            padding: `0 ${theme.space.s32}px`,
            width: '100%',
            height: '64px',
            outlineWidth: '2px',
            outlineOffset: '-2px',
            '&:hover': {
              backgroundColor: theme.colors.coolGrey[5],
            },
            '& > span > svg': {
              transition: 'transform 0.317s',
              transitionTimingFunction: theme.beziers.slowStartEase,
              transform: 'rotate(0deg)',
            },
            [mqMin.tabletLandscape]: {
              height: '100%',
              padding: `0 ${theme.space.s8}px`,
              '&:hover': {
                backgroundColor: 'transparent',
              },
            },
          },
          !isTopNav && {
            backgroundColor: theme.colors.white,
          },
          isDropdownOpen && {
            backgroundColor: theme.colors.coolGrey[5],
            borderTopRightRadius: theme.radii.base,
            borderTopLeftRadius: theme.radii.base,
            '& > span > svg': {
              transform: 'rotate(180deg)',
            },
            [mqMin.tabletLandscape]: {
              backgroundColor: 'transparent',
            },
          },
        ]}
        // eslint-disable-next-line react/jsx-sort-props
        className={clsx(className, isDropdownOpen && 'dropdownBorder')}
        onBlur={handleOnBlur}
        onClick={handleOnClick}
        //@ts-ignore
        onMouseEnter={handleOnOpenHover}
        //@ts-ignore
        onMouseLeave={handleOnCloseHover}
        tabIndex={0}
        variant={variant}
      >
        {link.title}
        <Icon
          css={{
            position: 'relative',
            width: '16px',
            marginLeft: '5px',
            marginRight: '10px',
            [mqMin.tabletSmall]: {
              marginRight: 'unset',
            },
            [mqMin.tabletLandscape]: {
              top: '1px',
              width: '10px',
            },
          }}
          type="CarotDown"
        />
      </Link>
      <NavDropdownMenu
        isDropdownOpen={isDropdownOpen}
        isTopNav={isTopNav}
        link={link}
        onBlur={handleOnBlur}
        onFocus={handleOnFocus}
        onMouseEnter={handleOnOpenHover}
        onMouseLeave={handleOnCloseHover}
      />
    </>
  );
};

export { NavDropdown };
export default NavDropdown;
